import { UserPlus } from "lucide-react";
import type { FC, ReactNode } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

import { AssignedUsersAvatarStack } from "../../../foreignNationals/components/AssignedUsersAvatarStack";
import type { TeamMember } from "../useCaseDetails";
import { Chip } from "./chip";

type PickerProps = {
  assigned: string[];
  team: TeamMember[];
  isLoading: boolean;
  isSaving: boolean;
  onChange: (userIds: string[]) => void | Promise<void>;
};

/**
 * Team-member checklist. `children` is the trigger subtree and must contain a
 * `<DropdownMenuTrigger asChild>`.
 */
export const AssigneesMenu: FC<
  PickerProps & { align?: "start" | "end"; children: ReactNode }
> = ({
  assigned,
  team,
  isLoading,
  isSaving,
  onChange,
  align = "end",
  children
}) => (
  <DropdownMenu>
    {children}
    <DropdownMenuContent align={align} className="w-72">
      <DropdownMenuLabel className="text-xs text-muted-foreground">
        Assigned team members
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      {isLoading ? (
        <div className="flex flex-col gap-2 px-2 py-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-36" />
        </div>
      ) : team.length === 0 ? (
        <div className="px-2 py-2 text-xs text-muted-foreground">
          No team members found.
        </div>
      ) : (
        team.map((m) => {
          const checked = assigned.includes(m.uid);
          return (
            <DropdownMenuCheckboxItem
              key={m.uid}
              checked={checked}
              disabled={isSaving}
              className="text-xs"
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={(next) =>
                onChange(
                  next
                    ? [...assigned, m.uid]
                    : assigned.filter((uid) => uid !== m.uid)
                )
              }
            >
              <Avatar size="sm">
                {m.avatarUrl ? <AvatarImage src={m.avatarUrl} alt="" /> : null}
                <AvatarFallback className="text-xs">
                  {m.initials}
                </AvatarFallback>
              </Avatar>
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{m.label}</span>
                {m.email ? (
                  <span className="truncate text-xs text-muted-foreground">
                    {m.email}
                  </span>
                ) : null}
              </span>
            </DropdownMenuCheckboxItem>
          );
        })
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);

export const AssigneesChip: FC<PickerProps & { className?: string }> = ({
  className,
  ...picker
}) => {
  const names = picker.team
    .filter((m) => picker.assigned.includes(m.uid))
    .map((m) => m.label);
  const tooltip =
    names.length > 0
      ? `Assigned to ${names.join(", ")}`
      : picker.assigned.length > 0
      ? `${picker.assigned.length} assigned`
      : "Assign team members";
  return (
    <AssigneesMenu {...picker}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Chip
              aria-label={tooltip}
              disabled={picker.isSaving}
              className={cn(
                "px-1.5",
                // With avatars there is no bordered pill - just the stack.
                picker.assigned.length > 0 &&
                  "border-transparent bg-transparent px-0 hover:bg-transparent",
                className
              )}
            >
              {picker.assigned.length > 0 ? (
                <span className="flex items-center [&_[aria-label]>div]:size-5! [&_[aria-label]_img]:size-5! [&_[aria-label]_span]:text-xs!">
                  <AssignedUsersAvatarStack
                    userIds={picker.assigned}
                    maxVisible={3}
                    nonInteractive
                  />
                </span>
              ) : (
                <UserPlus className="size-3.5 opacity-70" />
              )}
            </Chip>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">{tooltip}</TooltipContent>
      </Tooltip>
    </AssigneesMenu>
  );
};
